import * as dc from 'dc'

// Centralizes the safeRenderAll/safeRedrawAll/scheduleRedraw trio that was
// copy-pasted near-identically in Conversions.vue and Overview.vue. Each dc
// chart's render()/redraw() is called in its own try/catch so one chart
// throwing never strands its siblings mid-registry-iteration - dc.redrawAll
// has no per-chart isolation of its own, so one throwing chart would
// otherwise silently skip every chart registered after it in
// dc.chartRegistry.list(group).
export function safeRenderAll (chartGroup) {
  dc.chartRegistry.list(chartGroup).forEach(ch => {
    if (!ch || typeof ch.render !== 'function') return
    try { ch.render() } catch (e) { console.warn('[FlowSight] chart render failed', ch?.anchorName?.(), e) }
  })
}

export function safeRedrawAll (chartGroup) {
  dc.chartRegistry.list(chartGroup).forEach(ch => {
    if (!ch || typeof ch.redraw !== 'function') return
    try { ch.redraw() } catch (e) { console.warn('[FlowSight] chart redraw failed', ch?.anchorName?.(), e) }
  })
}

// Returns a debounced redraw function bound to one chartGroup + one "is this
// tab currently active" flag getter, replacing each component's hand-rolled
// _debounce()+scheduleRedraw() pair. Call once per component instance
// (e.g. in created()) and reuse the returned function everywhere the old
// this.scheduleRedraw() was called.
export function createScheduledRedraw (chartGroup, isActiveFn) {
  let scheduled = false
  return function scheduleRedraw () {
    if (scheduled) return
    scheduled = true
    requestAnimationFrame(() => {
      scheduled = false
      const charts = dc.chartRegistry.list(chartGroup)
      if (!charts.length || !isActiveFn()) return
      safeRedrawAll(chartGroup)
    })
  }
}

// Wraps a single ResizeObserver so any number of chart-container elements
// share ONE debounced callback. Container width/height can change for
// reasons a 'resize' window listener never fires for - most notably a
// collapsible sidebar (FlowSightSideMenu's SideBarMenu) whose width is a
// pure CSS transition with no JS event/store hook of its own (see
// SideBarMenu.vue's `.sbm-root { transition: width .25s ease }`) - so
// observing the actual chart container's box is the only reliable source of
// truth for "did my available space change" (this mirrors the existing
// ResizeObserver pattern in ToolBar.vue's observeSlotWidth()).
//
// The observer fires on EVERY intermediate frame of a CSS width transition,
// which would otherwise thrash dc.js with a full resize+redraw per frame -
// so callbacks are debounced (trailing edge): each new firing resets the
// timer, and onSettled() only runs once resize activity has been quiet for
// debounceMs, which in practice lands right after a transition finishes.
export function createResizeWatcher (onSettled, debounceMs = 150) {
  let timer = null
  const observer = new ResizeObserver(() => {
    clearTimeout(timer)
    timer = setTimeout(onSettled, debounceMs)
  })
  return {
    observe (el) { if (el) observer.observe(el) },
    unobserve (el) { if (el) observer.unobserve(el) },
    disconnect () {
      clearTimeout(timer)
      observer.disconnect()
    },
  }
}
